import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Snackbar,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { CloseOutlined, DateRangeOutlined, EditOutlined, LanguageOutlined, SaveOutlined } from "@mui/icons-material";
import SurfaceCard from "../common/ui/SurfaceCard";
import FormField from "../common/ui/FormField";
import ErrorAlert from "../common/ErrorAlert";
import { useError } from "../../error/useError";
import { useUserProfileContext } from "../../contexts/ProfileContext";
import { UserProfileSettings } from "../../models/user/UserProfile";

type DateFormatOption = {
  label: string;
  value: string;
  example: string;
};

const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  { label: "MM/DD/YYYY", value: "MM/DD/YYYY", example: "12/31/2025" },
  { label: "DD/MM/YYYY", value: "DD/MM/YYYY", example: "31/12/2025" },
  { label: "YYYY-MM-DD", value: "YYYY-MM-DD", example: "2025-12-31" },
];

const LANGUAGE_OPTIONS = [{ label: "English", value: "en" }];

const AccountSettingsSection: React.FC = () => {
  const theme = useTheme();

  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const { error, helpText, setError, clearError, handleError } = useError();

  const [dateFormat, setDateFormat] = useState<string>("");
  const [language] = useState<string>("en");

  const {
    userProfileSettings,
    refetchProfile,
    updateProfileSettings,
    isSavingProfileSettings,
  } = useUserProfileContext();

  useEffect(() => {
    if (userProfileSettings) {
      setDateFormat(userProfileSettings.dateFormat);
    }
  }, [userProfileSettings]);

  const handleSave = () => {
    if (!dateFormat.trim()) {
      setError("Date format is required");
      return;
    }

    const updatedProfileSettings: UserProfileSettings = {
      profileId: userProfileSettings?.profileId,
      dateFormat,
    };

    updateProfileSettings(updatedProfileSettings, {
      onSuccess: () => {
        setSuccess("Account settings updated successfully!");
        setEditing(false);
        refetchProfile();
      },
      onError: (apiError) => {
        handleError(apiError);
      },
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setDateFormat(userProfileSettings?.dateFormat || "");
    clearError();
  };

  const selectedOption = DATE_FORMAT_OPTIONS.find((opt) => opt.value === dateFormat);

  return (
    <Box sx={{ p: { xs: 1.2, md: 1.8 } }}>
      <ErrorAlert
        error={error}
        helpText={helpText}
        onClear={clearError}
        onRetry={() => refetchProfile()}
        title="Account Settings Error"
      />

      <SurfaceCard
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
        }}
      >
        <Box sx={{ p: { xs: 2, md: 2.6 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 2.4 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Account Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose how dates are displayed in your account.
              </Typography>
            </Box>

            {!editing ? (
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => setEditing(true)}
                sx={{
                  textTransform: "none",
                  borderColor: alpha(theme.palette.info.main, 0.45),
                  color: theme.palette.info.light,
                }}
              >
                Edit
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<SaveOutlined />}
                  onClick={handleSave}
                  disabled={isSavingProfileSettings}
                  sx={{ textTransform: "none" }}
                >
                  {isSavingProfileSettings ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CloseOutlined />}
                  onClick={handleCancel}
                  disabled={isSavingProfileSettings}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Stack>

          <FormField
            select
            fullWidth
            label="Preferred date format"
            value={dateFormat}
            onChange={(e) => setDateFormat(e.target.value)}
            disabled={!editing}
            helperText={editing ? "Select your preferred date format." : "Click edit to change date format."}
            sx={{ mb: 1.8 }}
          >
            {DATE_FORMAT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label} - {option.example}
              </MenuItem>
            ))}
          </FormField>

          <FormField
            select
            fullWidth
            label="App language"
            value={language}
            disabled
            helperText="Multilingual support is planned for a future update."
            sx={{ mb: 1.8 }}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </FormField>

          <Box
            sx={{
              p: 1.2,
              borderRadius: "10px",
              border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
              background: alpha(theme.palette.warning.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.8,
            }}
          >
            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
              <LanguageOutlined fontSize="small" sx={{ color: theme.palette.warning.light }} />
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                Language selection is currently fixed to English.
              </Typography>
            </Stack>
            <Chip size="small" label="Coming soon" color="warning" variant="outlined" />
          </Box>

          {selectedOption && (
            <Box
              sx={{
                p: 1.2,
                borderRadius: "10px",
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                background: alpha(theme.palette.info.main, 0.08),
                display: "flex",
                alignItems: "center",
                gap: 0.8,
              }}
            >
              <DateRangeOutlined fontSize="small" sx={{ color: theme.palette.info.light }} />
              <Typography variant="caption" color="text.secondary">
                Preview: {selectedOption.example} ({selectedOption.label})
              </Typography>
            </Box>
          )}
        </Box>
      </SurfaceCard>

      <Snackbar
        open={!!success}
        autoHideDuration={4200}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountSettingsSection;
